import React from "react";
import Interweave, { Filter, Matcher } from "interweave";
import { UrlMatcher } from "interweave-autolink";
import {
  Heading,
  Text,
  Box,
  Table,
  Tr,
  Td,
  Tbody,
  UnorderedList,
  OrderedList,
  ListItem,
  Link,
} from "@chakra-ui/react";
import PropTypes from "prop-types";
import MathJax from "react-mathjax-preview";

import * as textBlockStyles from "./textBlock.module.css";

class ParagraphFilter extends Filter {
  node(name, node) {
    if (name === "p") {
      node.setAttribute("style", "margin-top: 8px;");
    }

    return node;
  }
}

class CitationMatcher extends Matcher {
  match(string) {
    return this.doMatch(string, /\([-\w .;,&öíäüßæ]+ \d{4}\)/, (matches) => {
      const firstAuthor = matches[0]
        .split(" ")[0]
        .replace("(", "")
        .replace(",", "");
      return {
        href: `#${firstAuthor}`,
      };
    });
  }

  createElement(children, props) {
    return <Link {...props}>{children}</Link>;
  }

  asTag() {
    return "a";
  }
}

class FigureMatcher extends Matcher {
  match(string) {
    return this.doMatch(
      string,
      /Fig\. 1[A-Z]{0,1}[,A-Z ]*|Figure 1[A-Z]{0,1}[,A-Z ]*/i,
      (matches) => ({
        onClick: this.options.setIsFigureOpen,
      })
    );
  }

  createElement(children, props) {
    return <Link {...props}>{children}</Link>;
  }

  asTag() {
    return "a";
  }
}

const convertHTML = (node, children) => {
  if (node.tagName === "TABLE") {
    return (
      <Box borderWidth="1px" borderRadius="lg" overflowX="auto">
        <Table variant="striped">{children}</Table>
      </Box>
    );
  }
  if (node.tagName === "TR") {
    return <Tr>{children}</Tr>;
  }
  if (node.tagName === "TD") {
    return (
      <Td
        colSpan={node.getAttribute("colspan")}
        rowSpan={node.getAttribute("rowspan")}
        up="0.5rem"
        maxWidth="500px"
      >
        {children}
      </Td>
    );
  }
  if (node.tagName === "TBODY") {
    return <Tbody>{children}</Tbody>;
  }

  if (node.tagName === "UL") {
    return <UnorderedList>{children}</UnorderedList>;
  }
  if (node.tagName === "OL") {
    return <OrderedList>{children}</OrderedList>;
  }
  if (node.tagName === "LI") {
    return <ListItem>{children}</ListItem>;
  }

  if (node.tagName === "math") {
    return <MathJax math={node} />;
  }

  if (node.tagName === "A") {
    if (!node.hasAttribute("href")) return <>{children}</>;
    return (
      <Link href={node.getAttribute("href")} isExternal color="teal">
        {children}
      </Link>
    );
  }
};

const TextBlock = (props) => {
  const { label, text, isHtml, setIsFigureOpen } = props;
  if (!text || text === "" || text === "<p></p>") return null;

  const formattedText = isHtml ? (
    <Box
      className={textBlockStyles.textBlock}
      textAlign="justify"
      fontFamily="Noto Serif, serif"
    >
      <Interweave
        content={text}
        filters={[new ParagraphFilter()]}
        matchers={[
          new UrlMatcher("url"),
          new CitationMatcher("citation"),
          new FigureMatcher("figure", { setIsFigureOpen }),
        ]}
        newWindow={true}
        transform={convertHTML}
      />
    </Box>
  ) : (
    <Text fontFamily="Noto serif, serif">{text}</Text>
  );

  return (
    <Box marginBottom="1rem">
      {label && (
        <Heading
          size="lg"
          fontWeight="normal"
          fontFamily="Noto sans, sans-serif"
        >
          {label}
        </Heading>
      )}
      {formattedText}
    </Box>
  );
};

TextBlock.propTypes = {
  label: PropTypes.string,
  text: PropTypes.string.isRequired,
  isHtml: PropTypes.bool,
};

TextBlock.defaultProps = {
  label: null,
  isHtml: false,
};

export default TextBlock;
